import type { AssistantType } from "../../types/assistant";
import { useAvailableModels } from "../../hooks/useAvailableModels";

/**
 * Everything that configures the conversation, gathered at the top.
 *
 * Choosing a model is not here: each column owns its own picker, so a top-bar one
 * only duplicated the first column's. What remains is the catalogue warning, which
 * is about the list itself rather than any one choice.
 */

export const CORPUS_OPTIONS = [
  { label: "Spaces & LMS", value: "spaces" as const },
  { label: "Past Projects", value: "projects" as const },
  { label: "PAAB Correspondence", value: "correspondence" as const },
];

export function AssistantTopBar({
  assistantType,
  setAssistantType,
  onNewChat,
  onClose,
  canAddChannel,
  onAddChannel,
  showSidebar,
  onToggleSidebar,
  showHistory,
  onToggleHistory,
  showHistoryButton,
  canCompareModels,
  classic,
}: {
  assistantType: AssistantType;
  setAssistantType: (type: AssistantType) => void;
  onNewChat: () => void;
  onClose?: () => void;
  canAddChannel: boolean;
  onAddChannel: () => void;
  showSidebar: boolean;
  onToggleSidebar: () => void;
  showHistory: boolean;
  onToggleHistory: () => void;
  /** History is parked — see AssistantSurface. */
  showHistoryButton: boolean;
  /** Non-testers see the assistant as it works today: one model, no picker, no rail. */
  canCompareModels: boolean;
  /**
   * The home-page modal. It keeps the plain header it always had, and renders the
   * corpus switcher above the composer rather than here.
   */
  classic: boolean;
}) {
  const { isFallback, providerErrors, isLoading } = useAvailableModels();


  return (
    <div className={`border-b ${classic ? "border-gray-200 dark:border-maven-gray-800 bg-gray-50 dark:bg-maven-gray-950" : "border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800"}`}>
      {/* Row 1 — identity and session actions */}
      <div className={`flex items-center justify-between gap-3 px-4 ${classic ? "py-3" : "pt-3"}`}>
        <div className="flex min-w-0 items-center gap-2">
          {showHistoryButton && (
          <button
            onClick={onToggleHistory}
            aria-pressed={showHistory}
            title="Past conversations"
            className={`rounded-md px-2 py-1 text-xs ${showHistory ? "bg-maven-gray-100 dark:bg-maven-gray-800 text-maven-gray-800 dark:text-maven-gray-100" : "text-maven-gray-500 dark:text-maven-gray-500 hover:text-maven-gray-800"}`}
          >
            History
          </button>
          )}
          <h2 className="truncate font-medium text-maven-gray-800 dark:text-maven-gray-100">Maven Knowledge Assistant</h2>
        </div>

        <div className="flex items-center gap-2 shrink-0">

          {canCompareModels && (
          <button
            onClick={onAddChannel}
            disabled={!canAddChannel}
            title={
              canAddChannel
                ? "Answer the same prompt with another model"
                : "Three models is the maximum"
            }
            className="flex items-center gap-1 rounded-md border border-maven-teal-500 px-2 py-1 text-xs font-medium text-maven-teal-700 hover:bg-maven-teal-100 disabled:cursor-not-allowed disabled:border-maven-gray-100 disabled:text-maven-gray-500"
          >
            + Model
          </button>
          )}

          {canCompareModels && (
          <button
            onClick={onToggleSidebar}
            aria-pressed={showSidebar}
            title="Model, tokens and cost for each turn"
            className={`rounded-md px-2 py-1 text-xs ${showSidebar ? "bg-maven-gray-100 dark:bg-maven-gray-800 text-maven-gray-800 dark:text-maven-gray-100" : "text-maven-gray-500 dark:text-maven-gray-500 hover:text-maven-gray-800"}`}
          >
            Activity
          </button>
          )}


          <button
            onClick={onNewChat}
            className="text-xs text-maven-gray-500 dark:text-maven-gray-500 hover:text-maven-gray-800 px-2 py-1 rounded hover:bg-maven-gray-100"
          >
            New Chat
          </button>

          {onClose && (
            <button
              onClick={onClose}
              className="text-maven-gray-500 dark:text-maven-gray-500 hover:text-maven-gray-800 text-xl leading-none px-1"
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Honest about a stale list rather than pretending it is live. */}
      {(isFallback && !isLoading) || providerErrors ? (
        <div className="px-4 pt-2 text-[11px] text-maven-orange-700">
          {isFallback && !isLoading
            ? "Showing the bundled model list — the live catalogue did not load."
            : `Could not list models from ${Object.keys(providerErrors ?? {}).join(", ")}.`}
        </div>
      ) : null}

      {/* Row 2 — which corpus the question is asked against */}
      {!classic && (
      <div className="flex gap-1 px-4 py-3">
        {CORPUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setAssistantType(option.value)}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              assistantType === option.value
                ? "bg-maven-teal-500 text-white"
                : "bg-maven-gray-100 dark:bg-maven-gray-800 text-maven-gray-500 dark:text-maven-gray-500 hover:bg-maven-teal-100 hover:text-maven-gray-800"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      )}
    </div>
  );
}
